import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, PanelColorSettings, ContrastChecker, RichText } from '@wordpress/block-editor';
import { PanelBody, FormTokenField } from '@wordpress/components';
import './editor.scss';
import { useSelect } from '@wordpress/data';
import { RawHTML } from '@wordpress/element';

export default function Edit({ attributes, setAttributes }) {
	const { title, numberOfItems, postType, categories, selectedTitles, textColor, backgroundColor, btnBackgroundColor, btnTextColor } = attributes;

	const postTypes = useSelect((select) => {
		return select('core').getPostTypes();
	}, []);

	const catIds = categories && categories.length > 0 ? categories.map((cat) => cat.id) : [];
	const postTypeTaxonomy = postTypes && postTypes.length
		? postTypes.find((type) => type.slug === postType)?.taxonomies[0] || 'category'
		: '';
	const items = useSelect(
		(select) => {
			if (selectedTitles && selectedTitles.length > 0) {
				const titleArgs = {
					per_page: -1,
					_embed: true,
				};

				return select('core').getEntityRecords('postType', postType, titleArgs);
			}

			// If no titles are selected, fall back to other queries (category, etc.)
			const otherQueryArgs = {
				per_page: numberOfItems,
				_embed: true,
			};

			postType === 'post' ? (otherQueryArgs.categories = catIds) : (otherQueryArgs[postTypeTaxonomy] = catIds);

			return select('core').getEntityRecords('postType', postType, otherQueryArgs);
		},
		[postType, catIds, postTypeTaxonomy, numberOfItems, selectedTitles]
	)

	const allCats = useSelect(
		(select) => {
			return postTypeTaxonomy
				? select('core').getEntityRecords('taxonomy', postTypeTaxonomy, {
					per_page: -1,
				})
				: [];
		},
		[postTypeTaxonomy]
	);

	const catSuggestions = {};
	//For other post type
	if (allCats) {
		for (let i = 0; i < allCats.length; i++) {
			const cat = allCats[i];
			catSuggestions[cat.name] = cat;
		}
	}
	const defaultImage = `${artcloud_MAIN.artcloudThemePath}/${artcloud_MAIN.themeSlug}/assets/img/default-image.jpg`;
	const onChangeTitle = (value) => {
		setAttributes({ title: value });
	};
	const onChangeBackgroundColor = (newBgColor) => {
		setAttributes({ backgroundColor: newBgColor });
	};

	const onChangeTextColor = (newTextColor) => {
		setAttributes({ textColor: newTextColor });
	};
	const onChangebtnTextColor = (newTextColor) => {
		setAttributes({ btnTextColor: newTextColor });
	};
	const onChangeBtnBackgroundColor = (newBgColor) => {
		setAttributes({ btnBackgroundColor: newBgColor });
	};

	const onSelectTitles = (selected) => {
		const titleObjs = selected.map(title => {
			const foundEvent = allEventTitles.find(event => event.title === title);
			return foundEvent ? foundEvent : { id: null, title: title };
		});
		setAttributes({ selectedTitles: titleObjs });
	};

	const filteredItems = (items || []).filter(item =>
		!selectedTitles || selectedTitles.length === 0 || selectedTitles.some(titleObj => titleObj.title === item.title.rendered)
	);

	const allEventTitles = useSelect(
		(select) => {
			const baseArgs = { per_page: -1 };

			if (catIds && catIds.length > 0) {
				baseArgs['categories'] = catIds;
			}
			const allEvents = select('core').getEntityRecords('postType', postType, baseArgs);
			return allEvents ? allEvents.map(event => ({ id: event.id, title: event.title.rendered })) : [];
		},
		[catIds]
	);

	const excludedSlugs = [
		'page', 'attachment', 'nav_menu_item',
		'wp_block', 'wp_template', 'wp_template_part', 'wp_navigation'
	];
	//sort event by selected title wise
	function sortEventsBySelectedTitles(events, selectedTitles) {
		const sortedEvents = [];

		// Iterate through the selectedTitles and find matching events
		selectedTitles.forEach((titleObj) => {
			const matchingEvent = events.find((event) => titleObj.title === event.title.rendered);
			if (matchingEvent) {
				sortedEvents.push(matchingEvent);
			}
		});

		// Add remaining events that were not in selectedTitles
		events.forEach((event) => {
			if (!selectedTitles.some((titleObj) => titleObj.title === event.title.rendered)) {
				sortedEvents.push(event);
			}
		});

		return sortedEvents;
	}
	const sortedItems = sortEventsBySelectedTitles(filteredItems, selectedTitles);

	return (
		<div {...useBlockProps()}>
			<InspectorControls>
				<PanelBody>
					<FormTokenField
						label="Select Events by Title"
						value={selectedTitles.map(titleObj => titleObj.title)}
						suggestions={allEventTitles.map(event => event.title)}
						onChange={onSelectTitles}
					/>

				</PanelBody>
				<PanelColorSettings
					title={__('Color Settings', 'artcloud-events')}
					icon="admin-appearance"
					initialOpen
					colorSettings={[
						{
							value: backgroundColor,
							onChange: onChangeBackgroundColor,
							label: __('Background Color', 'artcloud-events')
						},
						{
							value: textColor,
							onChange: onChangeTextColor,
							label: __('Text Color', 'artcloud-events')
						},
						{
							value: btnBackgroundColor,
							onChange: onChangeBtnBackgroundColor,
							label: __('Button Background Color', 'artcloud-events')
						},
						{
							value: btnTextColor,
							onChange: onChangebtnTextColor,
							label: __('Button Text Color', 'artcloud-events')
						},
					]}
				>
					<ContrastChecker textColor={textColor} backgroundColor={backgroundColor} />
				</PanelColorSettings>
			</InspectorControls>
			<RichText
				tagName="h1"
				style={{ color: btnBackgroundColor }}
				className="event-block-title"
				placeholder={__('Event Block Title', 'artcloud-events')}
				value={title}
				onChange={onChangeTitle}
				allowedFormats={[]}
			/>
			<div className='row'>
				{sortedItems &&
					sortedItems.map((event) => {
						const eventContent = event.excerpt.rendered;
						const eventExcerpt = eventContent.split(' ').slice(0, 16).join(' ');
						const dateOptions = {
							weekday: 'long',
							year: 'numeric',
							month: '2-digit',
							day: '2-digit',
							// timeZone: 'UTC'
						};
						const formattedDate = new Date(event.start_date).toLocaleDateString('fi-FI', dateOptions);
						const capitalizedDate = formattedDate.replace(/^\w/, (c) => c.toUpperCase());
						return (
							<div className="event-section" style={{ backgroundColor: backgroundColor, color: textColor }}>
								<div className="event-content">
									<h3 className="event-title">{event.title.rendered}</h3>
									<h5 className='event-time'>
										{event.start_date == event.end_date ? `${capitalizedDate} @ ${event.start_time} - ${event.end_time}`
											: `${capitalizedDate} @ ${event.start_time} - ${event.end_date} @ ${event.end_time}`}
									</h5>
									<h6 className="event-place">{`${event.address}`}</h6>
									<RawHTML>{eventExcerpt}</RawHTML>
									<div className="event-button">
										<a href={event.link} className="event-button excerpt__more-link" style={{ backgroundColor: btnBackgroundColor, color: btnTextColor }}>{__('Show', 'artcloud-events')}</a>
									</div>
								</div>
								<div className="event-image">
									<img src={event?.thumbnail_url || defaultImage}
										alt={event?.title?.rendered || 'event default image'} />
								</div>
							</div>
						);
					})}
			</div>
		</div>
	);
}
